import { useState } from 'react';

import { WarningFilled } from '@ant-design/icons';
import { Button, Flex, Segemented, Colors, Text } from '@revenuemarket/cds';
import { ResponseStatus } from '@revenuemarket/http';
import { overlays } from '@revenuemarket/overlays';
import { Toast } from '@revenuemarket/ui';
import { assert, mixpanelTrack, omit } from '@revenuemarket/utils';
import { Form } from 'antd';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import { match } from 'ts-pattern';
import * as yup from 'yup';
import { useCreateCertificates } from '~hooks/queries/certificates/query';
import useCertificatesQuery from '~hooks/queries/useCertificatesQuery';
import { useCompanyId } from '~hooks/useCompanyId';
import { MIXPANEL_EVENT } from '~libs/mixpanel/event';
import { CodefCertificateHardDiskPath, ConnectedCodefCertificate } from '~models/certificate';
import { checkDuplicateCert } from '~utils/certificate';
import { extractP12PrivateCertificate, parsePEMCertificate } from '~utils/certificate/pfx';

import CommonModal from '../CommonModal';
import BusinessRegNumberInputModal from './BusinessRegNumberInputModal';
import CertificatePassword from './CertificatePassword';
import CertificateSuccessModal from './CertificateSuccessModal';
// TODO: Refactor to resolve circular dependency
// oxlint-disable-next-line import/no-cycle
import CertListRegisterSection from './CertListRegisterSection';
import FindCertSection from './FindCertSection';
import useCertificateSetup from './hooks/useCertificateSetup';
import USBRegisterSection from './USBRegisterSection';

const LoginScheme = yup.object({
  password: yup.string().required('비밀번호를 입력해주세요'),
});
interface CertificateLoginForm {
  password: string;
}

export enum CertificateRegisterType {
  DISK = 'disk',
  USB = 'usb',
  FIND = 'find',
}

const GAP = 32;

const CertificateSection = ({
  isLoading,
  setIsLoading,
}: {
  isLoading: boolean;
  setIsLoading: (value: boolean) => void;
}) => {
  const { companyId } = useCompanyId();
  const [form] = Form.useForm();

  const [passwordErrorExist, setPasswordErrorExist] = useState(false);
  const { data: certificate, isLoading: isCertificatesLoading } = useCertificatesQuery(companyId);
  const certificateList = certificate?.Data?.data?.certificates || [];

  const [selectedSegmentedFilter, setSelectedSegmentedFilter] = useState('disk');

  const { mutateAsync: createCertificates } = useCreateCertificates();
  const {
    certificates,
    drivePaths,
    drivePath,
    setDrivePath,
    requestExportCertificate,
    selectedCertificate,
    setSelectedCertificate,
    certificateByFind,
    setCertificateByFind,
  } = useCertificateSetup();

  const onConnect = async (certificate: ConnectedCodefCertificate) => {
    if (certificateList.some((value) => checkDuplicateCert(value, certificate))) {
      Toast.error('이미 등록된 공동인증서입니다.');
      return;
    }
    setIsLoading(true);
    const response = await createCertificates({
      companyId,
      request: {
        initScrape: false,
        certificates: [certificate],
      },
    });

    if (response.Status === ResponseStatus.SUCCESS) {
      const registeredCertificate = response.Data?.data?.certificates?.[0];

      if (registeredCertificate) {
        // businessRegNumber가 있으면 성공 모달, 없으면 입력 모달
        if (registeredCertificate.businessRegNumber) {
          overlays.open(({ isOpen, onClose }) => (
            <CertificateSuccessModal
              isOpen={isOpen}
              onClose={onClose}
              certificate={registeredCertificate}
            />
          ));
        } else {
          overlays.open(({ isOpen, onClose }) => (
            <BusinessRegNumberInputModal
              isOpen={isOpen}
              onClose={onClose}
              certificate={registeredCertificate}
              onSuccess={() => {
                // 사업자번호 입력 성공 후 재조회
                Toast.success('공동인증서가 등록됐어요');
              }}
            />
          ));
        }
      }

      form.resetFields();
      setCertificateByFind(undefined);
      setSelectedCertificate(undefined);
    } else {
      if (response.ErrorCode === 'NO_LINKED_ORGANIZATION') {
        overlays.open(({ isOpen, onClose }) => (
          <CommonModal
            width={400}
            open={isOpen}
            onCancel={onClose}
            footer={null}
            title={
              <Flex align="center" gap={4}>
                <WarningFilled style={{ fontSize: 24, color: Colors.orange400 }} />
                <Text textStyle="16_600" style={{ color: Colors.gray800 }}>
                  연동 가능한 기관이 없어요
                </Text>
              </Flex>
            }
          >
            <Flex direction="column" gap={33} style={{ paddingTop: 14 }}>
              <Text textStyle="14_400" style={{ color: Colors.gray800 }}>
                해당 공동인증서로 연동 가능한 기관을 찾을 수 없어요.{' '}
              </Text>
              <Button
                border
                onClick={onClose}
                buttonSize="large"
                buttonType="weak"
                style={{
                  alignSelf: 'flex-end',
                }}
              >
                닫기
              </Button>
            </Flex>
          </CommonModal>
        ));
        return;
      }
      Toast.error('공동인증서 등록에 실패했습니다.');
    }
    setIsLoading(false);
  };

  const onFinish = async (values: CertificateLoginForm) => {
    try {
      assert(selectedCertificate != null);
      setIsLoading(true);

      const certificate = await requestExportCertificate({
        certPassword: values.password,
        certPath: selectedCertificate.raw['cert.der.path'],
        keyPath: selectedCertificate.raw['cert.key.path'],
      });
      await onConnect({
        ...omit(selectedCertificate, ['raw', 'status']),
        cert: certificate.CONVERT,
        password: values.password,
      });
    } catch {
      setPasswordErrorExist(true);
    } finally {
      setIsLoading(false);
    }
  };

  const onFinishByFind = (values: CertificateLoginForm) => {
    try {
      mixpanelTrack(MIXPANEL_EVENT.CERTIFICATE.CLICK_REGISTER_CERTIFICATE);
      assert(certificateByFind != null);
      const privateCertificate = extractP12PrivateCertificate(
        certificateByFind.encoded,
        values.password,
      );
      const parsed = parsePEMCertificate(privateCertificate.certificate);
      assert(parsed != null);
      if (parsed.expireStatus === 'EXPIRED') {
        Toast.error('만료된 공동인증서입니다. 다른 공동인증서를 선택해주세요');
        setCertificateByFind(undefined);
        return;
      }

      const codefCertificate: ConnectedCodefCertificate = {
        certificateExpireDate: parsed.expireDate,
        certificateOwner: parsed.subjectCN,
        cert: certificateByFind.encoded,
        password: values.password,
        certificateAuthority: parsed.simpleOIDName,
        certificateType: match(parsed.organizationUnit)
          .with('corporation', () => '은행기업')
          .with('personal4IB', () => '은행개인')
          .otherwise(() => '은행개인'),
      };
      onConnect(codefCertificate);
    } catch {
      setPasswordErrorExist(true);
    }
  };

  return (
    <Flex direction="column" gap={24} style={{ width: 500 }}>
      <Segemented
        size="large"
        style={{ width: 'max-content' }}
        options={[
          { label: '하드디스크', value: CertificateRegisterType.DISK },
          { label: '이동식디스크', value: CertificateRegisterType.USB },
          { label: '직접 찾기', value: CertificateRegisterType.FIND },
        ]}
        value={selectedSegmentedFilter}
        onChange={(value) => {
          if (value === CertificateRegisterType.USB) {
            setDrivePath('submenu');
          } else {
            setDrivePath(CodefCertificateHardDiskPath);
          }
          setSelectedSegmentedFilter?.(value as string);
        }}
      />
      <Form
        form={form}
        onFinish={
          selectedSegmentedFilter === CertificateRegisterType.FIND ? onFinishByFind : onFinish
        }
        layout="vertical"
      >
        <OverlayScrollbarsComponent
          style={{
            paddingBottom: 24,
            maxHeight: `calc(100vh - ${GAP * 2}px - 306px)`,
            overflowY: 'scroll',
          }}
        >
          {selectedSegmentedFilter === CertificateRegisterType.DISK && (
            <CertListRegisterSection
              setType={setSelectedSegmentedFilter}
              certificates={certificates}
              registeredCertificates={certificateList}
              setSelectedCertificate={(certificate) => {
                if (selectedCertificate === certificate) {
                  setSelectedCertificate(undefined);
                } else {
                  setSelectedCertificate(certificate);
                }
              }}
              isLoading={isCertificatesLoading}
              selectedCertificate={selectedCertificate}
            />
          )}
          {selectedSegmentedFilter === CertificateRegisterType.USB && (
            <USBRegisterSection
              drivePaths={drivePaths}
              drivePath={drivePath}
              setDrivePath={setDrivePath}
              certificates={certificates}
              registeredCertificates={certificateList}
              setSelectedCertificate={setSelectedCertificate}
              selectedCertificate={selectedCertificate}
              setType={setSelectedSegmentedFilter}
              isLoading={isCertificatesLoading}
            />
          )}
          {selectedSegmentedFilter === CertificateRegisterType.FIND && (
            <FindCertSection
              certificate={certificateByFind}
              setCertificate={setCertificateByFind}
            />
          )}
        </OverlayScrollbarsComponent>
        {(selectedSegmentedFilter !== CertificateRegisterType.FIND
          ? certificates.length > 0
          : !!certificateByFind) && (
          <CertificatePassword
            passwordErrorExist={passwordErrorExist}
            setPasswordErrorExist={setPasswordErrorExist}
            disabled={
              isLoading || selectedSegmentedFilter === CertificateRegisterType.FIND
                ? !certificateByFind
                : !selectedCertificate
            }
          />
        )}
        <Form.Item<CertificateLoginForm> dependencies={['password']}>
          {({ getFieldsValue }) => {
            const valid = (() => {
              try {
                LoginScheme.validateSync(getFieldsValue());
                return true;
              } catch {
                return false;
              }
            })();

            return (
              <Button
                buttonSize="xlarge"
                buttonType="primary"
                type="submit"
                style={{ width: '100%' }}
                disabled={!valid}
                loading={isLoading}
              >
                연동하기
              </Button>
            );
          }}
        </Form.Item>
      </Form>
    </Flex>
  );
};

export default CertificateSection;
